import { useEffect, useMemo, useRef, useState } from "react";
import { InfiniteData, useInfiniteQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { SeaOrderQuery, SeaWhereQuery } from "../../../api/query.types";
import { SavedShipmentResponseDTO } from "../../../api/response.types";
import { CountBySeaStatusModel } from "../../../model/types";
import { useRequestCallbacks } from "shared/api/lib/api.hooks";
import { CARDS_REQUEST_LIMIT } from "shared/constants/common.constants";
import { Nullable } from "shared/lib/typeCheck.lib";

import { savedShipmentService } from "../../../api/api";
import { SeaSavedCardModel } from "../../../model/model";

type ParseResponseFuncReturnType = {
  page: SeaSavedCardModel[];
  pages: SeaSavedCardModel[][];
  totalCount: number;
  countByStatus: CountBySeaStatusModel[];
};

const parseResponse = (response?: SavedShipmentResponseDTO[]): ParseResponseFuncReturnType => {
  const pages = response?.map((page) => page.cards.map((card) => new SeaSavedCardModel(card)));

  return {
    pages: pages ?? [],
    page: pages ? pages[pages.length - 1] : [],
    totalCount: response?.[response.length - 1].total_count ?? 0,
    countByStatus: response?.[response.length - 1].count_by_status ?? [],
  };
};

export type RequestFuncParams = { where: SeaWhereQuery; order: SeaOrderQuery[] };

type SearchError = Error;

type RequestMoreCardsFunc = () => void;
type ResetCardsQueryFunc = () => Promise<void>;
type SetRequestFilterSyncFunc = (filter: Nullable<Partial<RequestFuncParams>>) => void;

export type UseGetSavedCardsInfiniteQueryReturnType = {
  requestMoreCards: RequestMoreCardsFunc;
  cancelGetCardsRequest: () => Promise<void>;
  resetSeaCardsQuery: ResetCardsQueryFunc;
  setRequestFilterSync: SetRequestFilterSyncFunc;
  cards: SeaSavedCardModel[];
  countBySeaStatus: CountBySeaStatusModel[];
  canRequestMoreSeaCards: boolean;
  isCardsLoading: boolean;
  isCardsFetching: boolean;
  isCardsLoadingNextPage: boolean;
  refetchSeaCards: () => void;
};

export type UseSeaSavedCardsInfiniteQueryParams = Required<Pick<UseQueryOptions, "queryKey">> & {
  shouldRequestOnMount?: boolean | (() => boolean);
  onSuccess?: (params: {
    page: SeaSavedCardModel[];
    pages: SeaSavedCardModel[][];
    totalCount: number;
    countByStatus: CountBySeaStatusModel[];
  }) => void;
  onSettled?: (params: {
    page: SeaSavedCardModel[] | undefined;
    pages: SeaSavedCardModel[][] | undefined;
    totalCount: number;
    error: SearchError | null;
  }) => void;
  onBeforeRequest?: () => void;
  initialFilter?: Nullable<Partial<RequestFuncParams>>;
};

export const useSeaSavedCardsInfiniteQuery = ({
  onBeforeRequest,
  shouldRequestOnMount = true,
  queryKey,
  initialFilter,
  onSuccess,
  onSettled,
}: UseSeaSavedCardsInfiniteQueryParams): UseGetSavedCardsInfiniteQueryReturnType => {
  const queryClient = useQueryClient();
  const requestFuncParamsRef = useRef<Nullable<Partial<RequestFuncParams>>>(initialFilter);
  const [enabled, setEnabled] = useState(false);

  const meta = useInfiniteQuery<SavedShipmentResponseDTO, SearchError>({
    queryKey,
    enabled: enabled,
    queryFn: async ({ signal, pageParam = 0 }) => {
      onBeforeRequest?.();
      return await savedShipmentService.getCards(
        { limit: CARDS_REQUEST_LIMIT, offset: pageParam as number, ...requestFuncParamsRef.current },
        signal
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return false;
      const totalCount = lastPage.total_count;
      const currentCount = allPages.reduce((acc, page) => acc + page.cards.length, 0);
      if (currentCount >= totalCount) {
        return undefined;
      }
      return currentCount;
    },
  });

  const { data, fetchNextPage, hasNextPage = false, isLoading, isFetching, isFetchingNextPage, refetch } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<InfiniteData<SavedShipmentResponseDTO>, SearchError>(meta);

  const requestCallbacks = () => {
    setRequestCallbacks({
      onSuccess: (response) => onSuccess?.(parseResponse(response.pages)),
      onSettled: (response, error) => {
        const { page, pages, totalCount } = parseResponse(response?.pages);
        onSettled?.({
          page,
          pages,
          totalCount,
          error,
        });
      },
    });
  };

  useEffect(() => {
    requestCallbacks();
    if (typeof shouldRequestOnMount === "function") {
      setEnabled(shouldRequestOnMount());
      return;
    }
    setEnabled(shouldRequestOnMount);
  }, [shouldRequestOnMount]);

  const requestMoreCards: RequestMoreCardsFunc = () => {
    requestCallbacks();
    fetchNextPage();
  };

  const setRequestFilterSync: SetRequestFilterSyncFunc = (filter) => {
    requestFuncParamsRef.current = filter;
  };

  const resetCardsQuery: ResetCardsQueryFunc = () => {
    setRequestFilterSync(null);
    requestCallbacks();
    return queryClient.resetQueries({ queryKey });
  };

  const refetchCards = () => {
    requestCallbacks();
    return refetch();
  };

  const cancelGetCardsRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  const cardsResponse = useMemo(() => {
    const { page } = parseResponse(data?.pages);
    return page ?? [];
  }, [data?.pages.length]);

  const countByStatus = useMemo(() => {
    const { countByStatus } = parseResponse(data?.pages);
    return countByStatus ?? [];
  }, [data?.pages.length]);

  return {
    requestMoreCards,
    cancelGetCardsRequest,
    resetSeaCardsQuery: resetCardsQuery,
    setRequestFilterSync,
    refetchSeaCards: refetchCards,
    cards: cardsResponse,
    countBySeaStatus: countByStatus,
    canRequestMoreSeaCards: hasNextPage,
    isCardsLoading: isLoading,
    isCardsFetching: isFetching,
    isCardsLoadingNextPage: isFetchingNextPage,
  };
};
